// Styled-components wrappers for Inside and Outside layouts
import styled from 'styled-components';
import { Layout, Menu } from 'antd';
import { hexToRgba } from '@utils/colors';

export const LayoutContainer = styled.div`
    width: 100%;
    max-width: 100vw;
    height: 100vh;
    overflow: hidden;
    box-sizing: border-box;
    position: relative;
    display: flex;
`;

export const MainContentArea = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    width: 100%;
    height: 100vh;
    overflow: hidden;
    margin-left: ${({ $sidebarWidth, $isMobile }) => $isMobile ? 0 : `${$sidebarWidth}px`};
    transition: margin-left 0.2s;
    background: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? '#141414' : '#f0f0f0';
    }};
`;

export const BodyContainer = styled.div`
    flex: 1;
    width: 100%;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    overflow-x: hidden;
    position: relative;
    box-sizing: border-box;
    min-height: 0;
    
    /* Custom scrollbar styling */
    &::-webkit-scrollbar {
        width: 8px;
    }
    
    &::-webkit-scrollbar-track {
        background: transparent;
    }
    
    &::-webkit-scrollbar-thumb {
        background: ${({ theme }) => theme.colors.border.default};
        border-radius: 4px;
        
        &:hover {
            background: ${({ theme }) => theme.colors.text.secondary};
        }
    }
    
    /* Firefox scrollbar */
    scrollbar-width: thin;
    scrollbar-color: ${({ theme }) => theme.colors.border.default} transparent;
`;

export const ContentWrapper = styled.div`
    width: 100%;
    max-width: 100%;
    padding: ${({ theme }) => theme.spacing.lg} ${({ theme }) => theme.spacing.lg};
    display: flex;
    flex-direction: column;
    background: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? '#1a1a1a' : '#f0f0f0';
    }};
    box-sizing: border-box;
    overflow: visible;

    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
        padding: ${({ theme }) => theme.spacing.md};
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
        padding: ${({ theme }) => theme.spacing.sm};
    }
`;

export const HeaderWrapper = styled(Layout.Header)`
    background: ${({ theme, $isScrolled }) => {
        const isDark = theme.colors.background.default === '#141414';
        if ($isScrolled) {
            // Glassy effect with semi-transparent background
            return isDark ? 'rgba(41, 41, 41, 0.6)' : 'rgba(255, 255, 255, 0.5)';
        }
        return isDark ? '#292929' : '#f0f0f0';
    }} !important;
    padding: 8px ${({ theme }) => theme.spacing.lg};
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 56px;
    min-height: 56px;
    position: sticky;
    top: 0;
    z-index: 100;
    backdrop-filter: ${({ $isScrolled }) => $isScrolled ? 'blur(20px) saturate(180%)' : 'none'};
    -webkit-backdrop-filter: ${({ $isScrolled }) => $isScrolled ? 'blur(20px) saturate(180%)' : 'none'};
    transition: background 0.3s ease, backdrop-filter 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;
    box-shadow: ${({ theme, $isScrolled }) => {
        if (!$isScrolled) return 'none';
        const isDark = theme.colors.background.default === '#141414';
        return isDark
            ? '0 2px 8px rgba(0, 0, 0, 0.3)'
            : '0 2px 8px rgba(0, 0, 0, 0.08)';
    }};
    border-bottom: ${({ theme, $isScrolled }) => {
        if (!$isScrolled) return 'none';
        const isDark = theme.colors.background.default === '#141414';
        return isDark
            ? '1px solid rgba(255, 255, 255, 0.1)'
            : '1px solid rgba(0, 0, 0, 0.06)';
    }};
    gap: ${({ theme }) => theme.spacing.md};
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    flex-shrink: 0;

    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
        padding: 8px ${({ theme }) => theme.spacing.md};
        height: 52px;
        min-height: 52px;
    }
`;

export const HeaderDivider = styled.div`
    width: 100%;
    height: 1px;
    flex-shrink: 0;
    background: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(15, 23, 42, 0.08)';
    }};
`;

export const HeaderContent = styled.div`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing.md};
    flex: 1;
    min-width: 0;
    justify-content: flex-start;

    @media (max-width: 1024px) {
        justify-content: center;
    }
`;

export const HeaderTitle = styled.h1`
    font-family: var(--font-family-lufga);
    font-weight: var(--font-weight-lufga-bold);
    font-size: 1.5rem;
    margin: 0;
    text-align: left;

    @media (max-width: 1024px) {
        text-align: center;
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
        font-size: 1.25rem;
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
        font-size: 1.125rem;
    }
`;

export const HeaderActions = styled.div`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing.md};
    flex-shrink: 0;
    justify-content: ${({ $isMobile }) => $isMobile ? 'flex-end' : 'flex-start'};

    @media (max-width: 1024px) {
        justify-content: flex-end;
    }
`;

export const ThemeToggleButton = styled.button`
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: start;
    justify-content: center;
    color: ${({ theme }) => theme.colors.text.secondary};
    transition: color 0.2s;
    font-size: 1.25rem;

    &:hover {
        color: ${({ theme }) => theme.colors.text.primary};
    }
`;

export const HeaderIconButton = styled.button`
    width: 36px;
    height: 36px;
    border-radius: 10px;
    border: none;
    background: transparent;
    color: ${({ theme }) => theme.colors.text.secondary};
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
        color: ${({ theme }) => theme.colors.text.primary};
        background: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.04)';
    }};
    }

    .anticon {
        font-size: 16px;
    }
`;

export const HeaderUserButton = styled.button`
    width: 36px;
    height: 36px;
    padding: 0;
    border-radius: 999px;
    border: none;
    background: transparent;
    color: ${({ theme }) => theme.colors.text.primary};
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
        background: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.04)';
    }};
    }
`;

export const UserMenuMeta = styled.div`
    padding: 6px 4px;
    line-height: 1.2;
`;

export const UserMenuEmail = styled.div`
    font-weight: 600;
    color: ${({ theme }) => theme.colors.text.primary};
`;

export const UserMenuRole = styled.div`
    margin-top: 4px;
    font-size: 12px;
    color: ${({ theme }) => theme.colors.text.secondary};
`;

export const SiderWrapper = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    background: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? '#292929' : theme.colors.background.default;
    }};
    border-right: 1px solid ${({ theme }) => theme.colors.border.default};
    transition: all 0.2s;
    padding: 0 14px;
    overflow-y: auto;
    overflow-x: hidden;
    position: relative;
    direction: rtl; /* RTL direction to position scrollbar on left */
    resize: none;
    
    /* Custom scrollbar styling - thin and light pink color, no arrows */
    &::-webkit-scrollbar {
        width: 2px !important;
        height: 100% !important;
        background: transparent !important;
        -webkit-appearance: none !important;
    }
    
    &::-webkit-scrollbar-track {
        background: transparent !important;
        border: none !important;
        margin: 0 !important;
        padding: 0 !important;
    }
    
    &::-webkit-scrollbar-thumb {
        background: #FFF0F1 !important;
        border-radius: 0 !important;
        cursor: pointer !important;
        border: none !important;
        min-height: 20px !important;
        
        &:hover {
            background: #FFF0F1 !important;
            opacity: 0.8 !important;
        }
    }
    
    /* Completely remove scrollbar buttons (up/down arrows) */
    &::-webkit-scrollbar-button {
        display: none !important;
        width: 0 !important;
        height: 0 !important;
        -webkit-appearance: none !important;
        background: transparent !important;
        visibility: hidden !important;
    }
    
    &::-webkit-scrollbar-button:start:decrement,
    &::-webkit-scrollbar-button:end:increment {
        display: none !important;
        width: 0 !important;
        height: 0 !important;
        background: transparent !important;
        visibility: hidden !important;
    }
    
    &::-webkit-scrollbar-button:vertical:start:decrement,
    &::-webkit-scrollbar-button:vertical:end:increment {
        display: none !important;
        width: 0 !important;
        height: 0 !important;
        background: transparent !important;
        visibility: hidden !important;
    }
    
    &::-webkit-scrollbar-button:single-button {
        display: none !important;
        width: 0 !important;
        height: 0 !important;
    }
    
    &::-webkit-scrollbar-button:double-button {
        display: none !important;
        width: 0 !important;
        height: 0 !important;
    }
    
    &::-webkit-scrollbar-corner {
        display: none !important;
        background: transparent !important;
        visibility: hidden !important;
    }
    
    /* Firefox scrollbar - thin and light pink color, no arrows */
    scrollbar-width: thin;
    scrollbar-color: #FFF0F1 transparent;

    /* Reset direction for all children to LTR */
    & > * {
        direction: ltr;
    }

    @media (max-width: 1024px) {
        display: none !important;
    }
`;

export const StickyTopSection = styled.div`
    position: sticky;
    top: 0;
    z-index: 1;
    background: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? '#292929' : theme.colors.background.default;
    }};
    padding-bottom: ${({ theme }) => theme.spacing.md};
`;

export const LogoContainer = styled.div`
    padding: 18px 0 14px 0;
    display: flex;
    align-items: flex-start;
    justify-content: flex-start;
    gap: ${({ theme }) => theme.spacing.sm};
    min-height: 50px;

    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
        padding: ${({ theme }) => theme.spacing.md};
        min-height: 56px;
    }
`;

export const SchoolBrandBlock = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;
    padding-top: 2px;
`;

export const SchoolNameText = styled.div`
    font-weight: 700;
    font-size: 14px;
    line-height: 1.2;
    color: ${({ theme }) => theme.colors.text.primary};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 140px;
`;

export const SchoolMetaRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    color: ${({ theme }) => theme.colors.text.secondary};
    font-size: 12px;
    line-height: 1.1;
    flex-wrap: nowrap;
    min-width: 0;
`;

export const SchoolRegionText = styled.div`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 90px;
`;

export const PlanCard = styled.div`
    width: 100%;
    border-radius: 12px;
    padding: 12px 12px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    background: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return hexToRgba(theme.colors.primary, isDark ? 0.18 : 0.10);
    }};
    border: 1px solid ${({ theme }) => hexToRgba(theme.colors.primary, 0.22)};
    box-sizing: border-box;
`;

export const PlanCardMeta = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
`;

export const PlanCardLabel = styled.div`
    font-size: 12px;
    line-height: 1.1;
    font-weight: 700;
    color: ${({ theme }) => theme.colors.primary};
`;

export const PlanCardName = styled.div`
    font-size: 13px;
    line-height: 1.2;
    font-weight: 700;
    color: ${({ theme }) => theme.colors.text.primary};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

export const PlanUpgradeButton = styled.button`
    border: none;
    cursor: pointer;
    padding: 8px 10px;
    border-radius: 10px;
    font-size: 12px;
    line-height: 1;
    font-weight: 700;
    background: ${({ theme }) => theme.colors.primary};
    color: #ffffff;
    flex-shrink: 0;
    transition: transform 0.08s ease, opacity 0.15s ease;

    &:hover {
        opacity: 0.95;
    }

    &:active {
        transform: scale(0.98);
    }
`;

export const LogoImage = styled.img`
    height: 48px;
    width: 48px;
    object-fit: contain;
    margin-top: 0;

    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
        height: 48px;
        width: 48px;
    }
`;

export const UserProfileSection = styled.div`
    padding: 0 20px;
    height: 64px;
    display: flex;
    align-items: center;
    justify-content: ${({ $collapsed }) => $collapsed ? 'center' : 'flex-start'};
    gap: ${({ theme, $collapsed }) => $collapsed ? 0 : theme.spacing.md};
    background: #F0F0F0;
    border: 1px solid #DCE1E5;
    border-radius: 8px;
    margin-top: 35px;
    cursor: pointer;
    transition: all 0.3s ease;
    box-sizing: border-box;
    
    &:hover {
        background: #F0F0F0;
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
        padding: 0 20px;
        height: 64px;
    }
`;

export const UserInfoWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing.md};
    flex: 1;
    min-width: 0;
    overflow: hidden;
    opacity: ${({ $collapsed }) => $collapsed ? 0 : 1};
    max-width: ${({ $collapsed }) => $collapsed ? 0 : '100%'};
    transition: opacity 0.3s ease, max-width 0.3s ease, gap 0.3s ease;
`;

export const DropdownIcon = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    color: inherit;
    font-size: 12px;
    flex-shrink: 0;
    transform: ${({ $isOpen }) => $isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
    transition: transform 0.3s ease;
    
    .anticon {
        font-size: 12px;
    }
`;

export const UserAvatar = styled.div`
    width: ${({ $collapsed }) => $collapsed ? '36px' : '36px'};
    height: ${({ $collapsed }) => $collapsed ? '36px' : '36px'};
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.primary};
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: ${({ $collapsed }) => $collapsed ? '0.875rem' : '0.875rem'};
    flex-shrink: 0;
    transition: all 0.3s ease;
`;

export const UserInfo = styled.div`
    display: flex;
    flex-direction: column;
    min-width: 0;
    flex: 1;
`;

export const UserName = styled.div`
    font-weight: 600;
    font-size: 0.875rem;
    color: ${({ theme }) => theme.colors.text.primary};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

export const UserRole = styled.div`
    font-size: 0.75rem;
    color: ${({ theme }) => theme.colors.text.secondary};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

export const MenuContainer = styled.div`
    margin-top: 18px;
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    padding-bottom: ${({ theme }) => theme.spacing.md};

    .ant-menu {
        background: transparent;
        border-right: none !important;

        .ant-menu-item-disabled, .ant-menu-submenu-disabled {
            pointer-events: auto !important;
            cursor: not-allowed !important;
        }

        /* Parent submenu (e.g. Attendance) should align like normal items */
        .ant-menu-submenu-title {
            border-radius: 6px;
            padding: 10px 12px !important;
            height: auto;
            line-height: 1.5;
            border-right: none !important;
            font-size: 15px;
            font-weight: 500;
            color: ${({ theme }) => theme.colors.text.secondary};
            margin: 0;
            display: flex;
            align-items: center;
            gap: 10px;

            .anticon {
                font-size: 18px;
                color: ${({ theme }) => theme.colors.text.secondary};
                margin-right: 0;
            }

            &:hover {
                background: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.03)';
    }};
                color: ${({ theme }) => theme.colors.text.primary};

                .anticon {
                    color: ${({ theme }) => theme.colors.text.primary};
                }
            }
        }

        /* Indent children only (Teacher/Staff) */
        .ant-menu-submenu .ant-menu-item {
            padding-left: 38px !important;
        }

        /* Remove extra indent/gutter on submenu containers */
        .ant-menu-submenu > .ant-menu-submenu-title {
            padding-left: 12px !important;
        }
        
        .ant-menu-item {
            border-radius: 6px;
            padding: 10px 12px !important;
            height: auto;
            line-height: 1.5;
            border-right: none !important;
            font-size: 15px;
            font-weight: 500;
            color: ${({ theme }) => theme.colors.text.secondary};
            margin: 0;
            display: flex;
            align-items: center;
            gap: 10px;
            
            &::after {
                display: none !important;
            }
            
            .anticon {
                font-size: 18px;
                color: ${({ theme }) => theme.colors.text.secondary};
                margin-right: 0;
            }
            
            &:hover {
                background: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.03)';
    }};
                color: ${({ theme }) => theme.colors.text.primary};
                
                .anticon {
                    color: ${({ theme }) => theme.colors.text.primary};
                }
            }
            
            &.ant-menu-item-selected {
                background-color: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        // Soft tinted pill (same "professional" opacity feel as before),
        // but derived from the current tenant primary color.
        return hexToRgba(theme.colors.primary, isDark ? 0.16 : 0.10);
    }} !important;
                color: ${({ theme }) => theme.colors.primary};
                font-weight: 600;
                border-right: none !important;
                
                &::after {
                    display: none !important;
                }
                
                .anticon {
                    color: ${({ theme }) => theme.colors.primary};
                    font-size: 18px;
                }
            }
        }

        /* Extra specificity to override AntD default grey selected bg */
        .ant-menu-item.ant-menu-item-selected {
            background-color: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return hexToRgba(theme.colors.primary, isDark ? 0.16 : 0.10);
    }} !important;
        }

        .ant-menu-submenu-selected > .ant-menu-submenu-title {
            background-color: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return hexToRgba(theme.colors.primary, isDark ? 0.12 : 0.08);
    }} !important;
            color: ${({ theme }) => theme.colors.primary} !important;
        }
    }
`;

export const SidebarFooter = styled.div`
    border-top: 1px solid ${({ theme }) => theme.colors.border.default};
    padding: 14px 0;
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

export const FooterBrandRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
`;

export const FooterBrandLogo = styled.img`
    width: 34px;
    height: 34px;
    object-fit: contain;
    flex-shrink: 0;
`;

export const FooterBrandText = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
`;

export const FooterBrandTitle = styled.div`
    font-weight: 700;
    font-size: 13px;
    line-height: 1.2;
    color: ${({ theme }) => theme.colors.text.primary};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

export const FooterVersionText = styled.div`
    font-size: 12px;
    line-height: 1.2;
    color: ${({ theme }) => theme.colors.text.secondary};
`;

export const DrawerFooter = styled.div`
    margin-top: auto;
    padding-top: 16px;
`;

export const CollapseButton = styled.button`
    background: transparent;
    border: none;
    cursor: pointer;
    padding: ${({ theme }) => theme.spacing.sm};
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${({ theme }) => theme.colors.text.secondary};
    transition: all 0.2s;
    border-radius: 8px;
    width: 32px;
    height: 32px;
    flex-shrink: 0;
    
    &:hover {
        color: ${({ theme }) => theme.colors.text.primary};
        background: ${({ theme }) => theme.colors.background.tertiary};
    }
    
    .anticon {
        font-size: 18px;
    }
`;

export const HeaderCollapseButton = styled.button`
    background: transparent;
    border: 1px solid ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? '#8A98A7' : '#8A98A7';
    }};
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? '#8A98A7' : '#8A98A7';
    }};
    transition: all 0.2s;
    border-radius: 8px;
    width: 32px;
    height: 32px;
    flex-shrink: 0;
    
    &:hover {
        color: ${({ theme }) => theme.colors.text.primary};
        background: transparent;
    }
    
    .anticon {
        font-size: 16px;
    }
`;

export const SidebarCollapseButton = styled.button`
    width: 100%;
    margin-top: 20px;
    padding: 12px;
    background: transparent;
    border: 1px solid ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? '#2C2C2C' : '#E5E5E5';
    }};
    border-radius: 6px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? '#8A98A7' : '#8A98A7';
    }};
    transition: all 0.2s;
    
    &:hover {
        background: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? '#2C2C2C' : '#F5F5F5';
    }};
        border-color: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? '#8A98A7' : '#8A98A7';
    }};
    }
    
    svg {
        width: 16px;
        height: 16px;
    }

    @media (max-width: 1024px) {
        display: none;
    }
`;

export const MobileDrawerButton = styled.button`
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? '#ffffff' : '#000000';
    }};
    transition: all 0.2s;
    border-radius: 8px;
    font-size: 20px;
    
    &:hover {
        background: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)';
    }};
    }
    
    &:active {
        transform: scale(0.95);
    }

    @media (min-width: 1025px) {
        display: none;
    }
`;

export const DrawerOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 999;
    opacity: ${({ $isOpen }) => $isOpen ? 1 : 0};
    visibility: ${({ $isOpen }) => $isOpen ? 'visible' : 'hidden'};
    transition: opacity 0.3s ease, visibility 0.3s ease;
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);

    @media (min-width: 1025px) {
        display: none;
    }
`;

export const MobileDrawer = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    height: 100vh;
    z-index: 1000;
    transform: translateX(${({ $isOpen }) => $isOpen ? '0' : '-100%'});
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 2px 0 8px rgba(0, 0, 0, 0.15);
    width: 100%;

    @media (min-width: 769px) and (max-width: 1024px) {
        width: 32.5% !important;
    }

    @media (max-width: 768px) {
        width: 100%;
    }

    @media (min-width: 1025px) {
        display: none;
    }
`;

export const DrawerContent = styled.div`
    height: 100%;
    width: 100%;
    background: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? '#292929' : '#ffffff';
    }};
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 20px;
    box-sizing: border-box;

    /* Custom scrollbar */
    &::-webkit-scrollbar {
        width: 6px;
    }
    
    &::-webkit-scrollbar-track {
        background: transparent;
    }
    
    &::-webkit-scrollbar-thumb {
        background: ${({ theme }) => theme.colors.border.default};
        border-radius: 3px;
        
        &:hover {
            background: ${({ theme }) => theme.colors.text.secondary};
        }
    }
    
    scrollbar-width: thin;
    scrollbar-color: ${({ theme }) => theme.colors.border.default} transparent;
`;

export const DrawerHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 24px;
    padding-bottom: 20px;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border.default};

    ${LogoImage} {
        height: 48px;
        width: 48px;
        object-fit: contain;
        margin: 0;
    }
`;

export const DrawerCloseButton = styled.button`
    background: transparent;
    border: 1px solid ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? '#8A98A7' : '#8A98A7';
    }};
    cursor: pointer;
    padding: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? '#8A98A7' : '#8A98A7';
    }};
    transition: all 0.2s;
    border-radius: 8px;
    width: 36px;
    height: 36px;
    flex-shrink: 0;
    
    &:hover {
        color: ${({ theme }) => theme.colors.text.primary};
        background: ${({ theme }) => {
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)';
    }};
        border-color: ${({ theme }) => theme.colors.text.primary};
    }
    
    .anticon {
        font-size: 18px;
    }
`;

export const OutsideContainer = styled.div`
    height: 100vh;
    min-height: 100vh;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    background: #ffffff;
    padding: ${({ theme }) => theme.spacing.xl};
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;

    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
        align-items: flex-start;
        padding: ${({ theme }) => theme.spacing.lg};
    }
`;

export const OutsideLayoutContent = styled(Layout.Content)`
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 100%;
    padding: 0;
    background: transparent;
`;

export const OutsideContent = styled.div`
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;

    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
        padding-bottom: 64px;
    }
`;

